'use client';

import {
  isChunkLoadError,
  recoverFromChunkError,
} from '@/layers/presentation/utils/chunkErrorRecovery';
import { useEffect } from 'react';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Last-resort boundary: catches errors thrown in the root layout itself, where
// the normal error.tsx boundaries do not apply. It must render its own <html>
// and <body>. Like the route boundaries, it self-heals stale-chunk failures
// with a single guarded reload (bug B11).
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const isChunk = isChunkLoadError(error);

  useEffect(() => {
    if (recoverFromChunkError(error)) return;

    console.error('Global error:', error);
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            padding: '1rem',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            {isChunk ? 'Updating to the latest version…' : 'Something went wrong'}
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem', maxWidth: '32rem' }}>
            {isChunk
              ? 'A new version is available. Reloading now. If this page does not refresh automatically, please reload manually.'
              : 'We apologize for the inconvenience. Please try again, and if the problem persists, contact our support team.'}
          </p>
          <button
            type="button"
            onClick={() => (isChunk ? window.location.reload() : reset())}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
          >
            {isChunk ? 'Reload' : 'Try Again'}
          </button>
        </div>
      </body>
    </html>
  );
}
